/**
 * ChatGatingFlow — in-chat eligibility screening for CaseWorkspacePage.
 *
 * Pure display component: renders completed Q&A pairs from gatingState plus
 * the current question with Yes/No answer buttons.
 *
 * No API calls here. CaseWorkspacePage owns the state machine and calls
 * the existing POST /v1/cases/:id/gate once all 8 answers are collected.
 * Gating Q&A is never written to chat_messages — these are virtual bubbles.
 */
import type { GateRequest } from "@/lib/api-types";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GatingQuestion {
  field: keyof GateRequest;
  text: string;
  /** Defaults to "Yes" */
  yesLabel?: string;
  /** Defaults to "No" */
  noLabel?: string;
}

/** Owned by CaseWorkspacePage, passed down as props. */
export interface GatingState {
  /** True while the flow is actively running (questions being asked). */
  active: boolean;
  /** Current question index (0–7). Equals GATING_QUESTIONS.length when all answered. */
  step: number;
  /** Accumulated answers — all 8 required before the gate API call fires. */
  answers: Partial<GateRequest>;
  /** True while POST /gate is in flight. */
  submitting: boolean;
  /** Non-null if the gate call failed. */
  error: string | null;
}

export const INITIAL_GATING_STATE: GatingState = {
  active: false,
  step: 0,
  answers: {},
  submitting: false,
  error: null,
};

// ─── Question definitions ─────────────────────────────────────────────────────
// Same 8 fields as GateRequest, ordered for natural conversation flow.
// Disqualifying questions (criminal, denial, removal) placed last to avoid
// alarming the user before they're invested in the process.

export const GATING_QUESTIONS: GatingQuestion[] = [
  {
    field: "currently_in_us",
    text: "Are you currently living in the United States?",
  },
  {
    field: "meets_lpr_duration",
    text: "Have you been a Permanent Resident (Green Card holder) for at least 5 years? If you're married to a U.S. citizen, 3 years is enough.",
  },
  {
    field: "continuous_us_residence",
    text: "Have you maintained the United States as your primary home throughout that time, without extended stays abroad?",
  },
  {
    field: "is_self_filing",
    text: "Are you filing this application yourself, rather than having a representative file on your behalf?",
    yesLabel: "Yes, myself",
    noLabel: "No, a representative",
  },
  {
    field: "married_to_usc",
    text: "Are you currently married to a U.S. citizen?",
  },
  {
    field: "has_criminal_history",
    text: "Have you ever been arrested, cited, charged, or convicted of any crime — including offenses where charges were later dropped or expunged?",
  },
  {
    field: "has_prior_uscis_denial",
    text: "Has USCIS ever denied a naturalization application you submitted?",
  },
  {
    field: "has_removal_proceedings",
    text: "Have you ever been placed in removal or deportation proceedings?",
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  step: number;
  answers: Partial<GateRequest>;
  submitting: boolean;
  error: string | null;
  onAnswer: (field: keyof GateRequest, value: boolean) => void;
  onRetry: () => void;
}

export function ChatGatingFlow({ step, answers, submitting, error, onAnswer, onRetry }: Props) {
  return (
    <div className="space-y-4 py-2">

      {/* Completed Q&A pairs */}
      {GATING_QUESTIONS.slice(0, step).map((q) => {
        const value = answers[q.field];
        const answerLabel =
          value === true  ? (q.yesLabel ?? "Yes") :
          value === false ? (q.noLabel  ?? "No")  : "—";

        return (
          <div key={q.field} className="space-y-2">
            <GatingAssistantBubble text={q.text} />
            <GatingUserBubble text={answerLabel} />
          </div>
        );
      })}

      {/* Current question — shown until all answered or while submitting */}
      {!submitting && !error && step < GATING_QUESTIONS.length && (
        <div className="space-y-3">
          <GatingAssistantBubble text={GATING_QUESTIONS[step].text} />

          {/* Answer buttons */}
          <div className="flex gap-2.5 pl-9">
            <button
              onClick={() => onAnswer(GATING_QUESTIONS[step].field, true)}
              className="flex-1 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl py-2.5 transition-colors"
            >
              {GATING_QUESTIONS[step].yesLabel ?? "Yes"}
            </button>
            <button
              onClick={() => onAnswer(GATING_QUESTIONS[step].field, false)}
              className="flex-1 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 active:bg-slate-300 rounded-xl py-2.5 transition-colors"
            >
              {GATING_QUESTIONS[step].noLabel ?? "No"}
            </button>
          </div>
        </div>
      )}

      {/* Loading — gate API call in flight */}
      {submitting && (
        <div className="flex gap-2.5">
          <AssistantIcon />
          <div className="bg-slate-50 border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3">
            <div className="flex gap-1 items-center py-0.5">
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:0ms]" />
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:150ms]" />
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:300ms]" />
            </div>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && !submitting && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-3.5 py-2.5 text-xs text-red-600 ml-9">
          {error}
          <button onClick={onRetry} className="ml-2 underline font-medium">
            Retry
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Bubble helpers ───────────────────────────────────────────────────────────

function AssistantIcon() {
  return (
    <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shrink-0 mt-0.5">
      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
      </svg>
    </div>
  );
}

function GatingAssistantBubble({ text }: { text: string }) {
  return (
    <div className="flex gap-2.5">
      <AssistantIcon />
      <div className="bg-slate-50 border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[80%]">
        <p className="text-sm text-slate-700 leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

function GatingUserBubble({ text }: { text: string }) {
  return (
    <div className="flex flex-row-reverse gap-2.5">
      <div className="bg-blue-600 text-white rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[50%]">
        <p className="text-sm font-medium leading-relaxed">{text}</p>
      </div>
    </div>
  );
}
